import { useState, type FormEvent } from "react";

type Status = "idle" | "submitting" | "success" | "error";

const FORM_ACTION = "https://readdy.ai/api/form/db8gnu1rh5tg6socjuag";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("contact_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢你的反馈！");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("提交成功，感谢你的反馈，我们会尽快查看！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          (typeof serverMsg === "string" && serverMsg) || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="about-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">意见反馈</h3>
      <p className="mt-1 text-sm text-foreground-500">
        对站点有任何建议或想看的影视内容，欢迎留言告诉我们。
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="请输入称呼"
            className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱地址
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
          反馈类型
        </label>
        <select
          id="fb-topic"
          name="topic"
          className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="资源报错">资源报错</option>
          <option value="功能体验">功能体验</option>
          <option value="合作洽谈">合作洽谈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          rows={4}
          required
          maxLength={500}
          placeholder="请输入你的建议或想法（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
        />
      </div>

      <input
        type="text"
        name="contact_alt"
        className="contact-field-extra"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-send-plane-line text-base" aria-hidden="true" />
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>

        {message && (
          <p
            role="status"
            className={`text-sm ${
              status === "success" ? "text-primary-600" : "text-accent-700"
            }`}
          >
            {status === "success" ? "✓ " : "! "}
            {message}
          </p>
        )}
      </div>
    </form>
  );
}